import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Card } from '@beeline/design-system-react/extracted-styles';

import { BookingInfo } from 'src/api/bookingsView';
import { labelsByBookingObjectType } from 'src/pages/AdministratorPage/consts';
import { toBookingObjectType } from 'src/pages/AdministratorPage/utils';
import { RoutePath } from 'src/routes/types';
import { getIsDesktop } from 'src/store/agent/selectors';
import { getUserPermissions } from 'src/store/user/selectors';
import { PlaceType } from 'src/types';
import { getDateTime } from 'src/utils/getDateTime';
import { isBookingAvailable } from 'src/utils/isBookingAvailable';
import { toAddress } from 'src/utils/toAddress';

import { DesktopBookingCard } from './DesktopBookingCard/DesktopBookingCard';
import { desktopBookingCardStyle } from './DesktopBookingCard/consts';
import { MobileBookingCard } from './MobileBookingCard/MobileBookingCard';
import { mobileBookingCardStyle } from './MobileBookingCard/consts';
import { BookingCardComponentProps } from './types';

const dateFormatter = new Intl.DateTimeFormat('ru', {
  day: 'numeric',
  month: 'long',
  weekday: 'short',
});

const timeFormatter = new Intl.DateTimeFormat('ru', {
  hour: 'numeric',
  minute: 'numeric',
});

export interface BookingCardProps {
  booking: BookingInfo;
}

export const BookingCard: FC<BookingCardProps> = ({ booking }) => {
  const isDesktop = useSelector(getIsDesktop);
  const permissions = useSelector(getUserPermissions)?.permissions;

  const title = `${labelsByBookingObjectType[toBookingObjectType(booking.placeType, booking.roomType)]} ${booking.placeTitle}`;

  const place = toAddress({
    town: booking.town.townName,
    building: booking.building.address,
    floor: booking.floor.title,
  });
  const bookedFrom = getDateTime({
    date: new Date(booking.bookedFrom),
    timezoneOffset: booking.town.timeZone,
  });
  const bookedTo = getDateTime({
    date: new Date(booking.bookedTo),
    timezoneOffset: booking.town.timeZone,
  });

  const date = dateFormatter.format(bookedFrom);
  const from = timeFormatter.format(bookedFrom);
  const to = timeFormatter.format(bookedTo);
  const time =
    booking.placeType === PlaceType.Room ? `${from} — ${to}` : undefined;

  const link = useMemo((): string | undefined => {
    if (!isBookingAvailable({ ...booking, permissions })) return;

    const path =
      booking.placeType === PlaceType.Desk
        ? RoutePath.DeskBooking
        : RoutePath.RoomBooking;
    const searchParams = new URLSearchParams({
      searched: JSON.stringify({
        ...booking,
        bookedFrom: bookedFrom.toISOString(),
        bookedTo: bookedTo.toISOString(),
      }),
    });
    return `/${path}?${searchParams.toString()}`;
  }, [bookedFrom, bookedTo, booking, permissions]);

  const { card, bookingCardStyle } = useMemo(() => {
    const props: BookingCardComponentProps = {
      link,
      title,
      place,
      date,
      time,
    };

    return {
      bookingCardStyle: isDesktop
        ? desktopBookingCardStyle
        : mobileBookingCardStyle,
      card: isDesktop ? (
        <DesktopBookingCard {...props} />
      ) : (
        <MobileBookingCard {...props} />
      ),
    };
  }, [date, isDesktop, link, place, time, title]);

  return (
    <Card border='default' style={bookingCardStyle}>
      {card}
    </Card>
  );
};
